import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { getModeAccent } from '../themes';
import { MODES } from '../hooks/useTimer';

const SIZE = 288;
const RING_R = 118;
const STROKE = 9;
const CIRCUMFERENCE = 2 * Math.PI * RING_R;

const SESSION_TITLE = {
  [MODES.FOCUS]: 'Focus session',
  [MODES.SHORT_BREAK]: 'Short break',
  [MODES.LONG_BREAK]: 'Long break',
};

const BREATHE = {
  [MODES.FOCUS]: 'fl-breathe',
  [MODES.SHORT_BREAK]: 'fl-breathe-soft',
  [MODES.LONG_BREAK]: 'fl-breathe-deep',
};

const TICK_MARKS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i * 30 - 90) * (Math.PI / 180);
  const outerR = RING_R + STROKE / 2 + 7;
  const innerR = outerR - (i % 3 === 0 ? 7 : 4);
  return {
    x1: SIZE / 2 + outerR * Math.cos(angle),
    y1: SIZE / 2 + outerR * Math.sin(angle),
    x2: SIZE / 2 + innerR * Math.cos(angle),
    y2: SIZE / 2 + innerR * Math.sin(angle),
    major: i % 3 === 0,
  };
});

const MotionBox = motion(Box);

const TimerRing = ({ progress, mode, remaining, theme, isRunning, completionFlash }) => {
  const filterId = `fl-glow-${mode}`;
  const gradId = `fl-grad-${mode}`;
  const shimmerId = `fl-shimmer-${mode}`;

  const accent = getModeAccent(theme, mode);
  const dashOffset = CIRCUMFERENCE * (1 - Math.max(0, Math.min(1, progress)));

  const mins = Math.floor(remaining / 60);
  const secs = Math.floor(remaining % 60);
  const timeStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

  const breatheClass = isRunning ? BREATHE[mode] : '';
  const haloDuration = mode === MODES.FOCUS ? 42 : mode === MODES.SHORT_BREAK ? 54 : 68;

  return (
    <Box position="relative" w={`${SIZE}px`} h={`${SIZE}px`} flexShrink={0}>
      {isRunning && <Box className="fl-timer-sweep" aria-hidden />}

      {/* Outer slow-rotating halo ring */}
      <Box
        position="absolute"
        left="50%"
        top="50%"
        w="108%"
        h="108%"
        marginLeft="-54%"
        marginTop="-54%"
        borderRadius="full"
        className="fl-halo-spin"
        pointerEvents="none"
        style={{
          opacity: isRunning ? 0.45 : 0.22,
          background: `conic-gradient(from 0deg, transparent 0%, ${accent}22 18%, transparent 38%, transparent 62%, ${accent}18 78%, transparent 100%)`,
          filter: 'blur(1px)',
          transition: 'opacity 0.8s ease',
        }}
      />

      {/* Mid aura — mode-tuned breathing */}
      <Box
        position="absolute"
        borderRadius="full"
        className={breatheClass}
        style={{
          inset: '18%',
          background: `radial-gradient(circle, ${accent} 0%, transparent 68%)`,
          filter: 'blur(42px)',
          opacity: isRunning ? 1 : 0.55,
          transition: 'opacity 0.6s ease',
        }}
      />

      {/* Orbiting micro-accent */}
      <MotionBox
        position="absolute"
        left="50%"
        top="50%"
        w={`${RING_R * 2 + 56}px`}
        h={`${RING_R * 2 + 56}px`}
        marginLeft={`-${RING_R + 28}px`}
        marginTop={`-${RING_R + 28}px`}
        animate={{ rotate: isRunning ? 360 : 0 }}
        transition={{
          duration: isRunning ? haloDuration : 0,
          repeat: isRunning ? Infinity : 0,
          ease: 'linear',
        }}
        pointerEvents="none"
      >
        <Box
          position="absolute"
          top={0}
          left="50%"
          w="5px"
          h="5px"
          borderRadius="full"
          marginLeft="-2.5px"
          style={{
            background: accent,
            boxShadow: `0 0 12px ${accent}`,
            opacity: isRunning ? 0.55 : 0.2,
          }}
        />
      </MotionBox>

      <AnimatePresence>
        {completionFlash && (
          <motion.div
            key="pulse"
            initial={{ scale: 0.88, opacity: 0.65 }}
            animate={{ scale: 1.55, opacity: 0 }}
            transition={{ duration: 1.35, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `2px solid ${accent}`,
            }}
          />
        )}
      </AnimatePresence>

      <svg
        width={SIZE}
        height={SIZE}
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden="true"
      >
        <defs>
          <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={accent} stopOpacity="1" />
          </linearGradient>
          <linearGradient id={shimmerId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.2" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="0.35" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {TICK_MARKS.map((t, i) => (
          <line
            key={i}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={accent}
            strokeWidth={t.major ? 1.5 : 0.75}
            strokeOpacity={t.major ? (isRunning ? 0.4 : 0.28) : (isRunning ? 0.22 : 0.14)}
            strokeLinecap="round"
          />
        ))}

        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={RING_R}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={STROKE - 1}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />

        {/* Shimmer echo ring (subtle, only while running) */}
        {isRunning && progress > 0.02 && (
          <circle
            cx={SIZE / 2} cy={SIZE / 2} r={RING_R}
            fill="none"
            stroke={`url(#${shimmerId})`}
            strokeWidth={3}
            strokeDasharray={`${CIRCUMFERENCE * 0.08} ${CIRCUMFERENCE * 0.92}`}
            strokeDashoffset={-dashOffset + CIRCUMFERENCE * 0.04}
            strokeLinecap="round"
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            className="fl-ring-shimmer-stroke"
            opacity={0.7}
          />
        )}

        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={RING_R}
          fill="none"
          stroke={accent}
          strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          filter={`url(#${filterId})`}
          style={{
            transition: 'stroke-dashoffset 0.35s ease, stroke 0.8s ease',
          }}
        />

        {progress > 0.01 && progress < 0.995 && (() => {
          const angle = (progress * 360 - 90) * (Math.PI / 180);
          const x = SIZE / 2 + RING_R * Math.cos(angle);
          const y = SIZE / 2 + RING_R * Math.sin(angle);
          return (
            <circle
              cx={x} cy={y} r={STROKE / 2 + 1.5}
              fill={accent}
              filter={`url(#${filterId})`}
              style={{ transition: 'all 0.35s ease' }}
            />
          );
        })()}
      </svg>

      <Box
        position="absolute"
        inset={0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        pt={{ base: '28px', md: '36px' }}
      >
        <Text
          className="fl-mono"
          fontSize={{ base: '3.8rem', md: '4.2rem' }}
          fontWeight="300"
          letterSpacing="-3px"
          lineHeight={1}
          style={{
            color: theme.textPrimary,
            textShadow: `0 0 36px ${accent}50`,
            transition: 'color 0.5s ease, text-shadow 0.5s ease',
          }}
        >
          {timeStr}
        </Text>

        {/* Space below the time before the session pill */}
        <Box textAlign="center" maxW="240px" pt={0} mt={{ base: '-8px', md: '-10px' }}>
          <Box
            px={3}
            minH="26px"
            py={0}
            borderRadius="full"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            style={{
              background: `${accent}0d`,
              border: `1px solid ${accent}2e`,
              boxShadow: isRunning ? `0 0 16px ${accent}18` : 'none',
              boxSizing: 'border-box',
              transition: 'all 0.8s ease',
            }}
          >
            <Text
              as="span"
              fontSize="10px"
              fontWeight="600"
              letterSpacing="0.18em"
              textTransform="uppercase"
              lineHeight="1"
              m={0}
              p={0}
              style={{
                color: accent,
                opacity: 0.88,
                transition: 'color 0.8s ease, opacity 0.8s ease',
              }}
            >
              {SESSION_TITLE[mode]}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TimerRing;
